// Rule-based American-English grapheme -> phoneme decoder.
// decodeWord('ship') -> [{ text:'sh', code:'sh' }, { text:'i', code:'i' }, { text:'p', code:'p' }]
//
// English is irregular, so this is best-effort: longest-match graphemes, magic-e,
// soft c, vowel teams, r-controlled, plus an exceptions list for common irregular
// words. Codes map to the app's phoneme library (see public/phonics/README.txt).
// Anything it gets wrong can be overridden by curating that word.

const VOWELS = 'aeiou'
const isVowel = (c) => VOWELS.includes(c)
const SHORT = { a: 'a', e: 'e', i: 'i', o: 'o', u: 'u' }
const LONG = { a: 'ae', e: 'ee', i: 'ie', o: 'oe', u: 'ue' }

// A plain consonant letter -> code (most map to themselves).
function consonant(ch) {
  if (ch === 'c') return 'k'
  if (ch === 'x') return 'ks'
  if (ch === 'q') return 'k'
  return ch // b d f g h j k l m n p r s t v w z
}
// The consonant just before a silent 'e' goes soft: face->s, page->j.
function magicEConsonant(ch) {
  if (ch === 'c') return 's'
  if (ch === 'g') return 'j'
  return consonant(ch)
}
function yCode(w, i) {
  if (i === 0) return 'y' // consonant /y/ (yes)
  if (i === w.length - 1) return /[aeiou]/.test(w.slice(0, i)) ? 'ee' : 'ie' // happy vs my
  return 'i' // gym, myth
}
function single(w, i) {
  const c = w[i], nx = w[i + 1] || ''
  if (isVowel(c)) return SHORT[c]
  if (c === 'c') return (nx === 'e' || nx === 'i' || nx === 'y') ? 's' : 'k' // soft c
  if (c === 'g') return 'g' // default hard g; soft g handled by exceptions
  if (c === 'y') return yCode(w, i)
  if (c === 'x') return 'ks'
  if (c === 'q') return 'k'
  return c
}

// Trigraphs (checked first).
const TRI = { igh: 'ie', tch: 'ch', dge: 'j', air: 'air', are: 'air', ear: 'ear', eer: 'ear', ore: 'or', oor: 'or' }
// Digraphs & vowel teams.
const DI = {
  ch: 'ch', sh: 'sh', th: 'th', ph: 'f', ck: 'k', ng: 'ng', wh: 'w', qu: 'kw',
  kn: 'n', wr: 'r', gn: 'n', // silent-letter digraphs (knee, wrap, gnome)
  ai: 'ae', ay: 'ae', ea: 'ee', ee: 'ee', ey: 'ee', ie: 'ie', oa: 'oe', oe: 'oe',
  ue: 'ue', ew: 'uu', ui: 'uu', oo: 'uu', ou: 'ow', ow: 'oe', oi: 'oi', oy: 'oi',
  au: 'o', aw: 'o', // GenAm cot–caught merger → short-o
  ar: 'ar', or: 'or', er: 'ur', ir: 'ur', ur: 'ur',
  // doubled consonants say one sound (FLOSS rule: ss ff ll zz; also bb dd gg…)
  bb: 'b', dd: 'd', ff: 'f', gg: 'g', ll: 'l', mm: 'm', nn: 'n', pp: 'p', rr: 'r', ss: 's', tt: 't', zz: 'z',
}

// word -> [[grapheme, code], ...]  (high-frequency irregulars & tricky spellings)
const EXC = {
  the: [['th', 'dh'], ['e', 'uh']], a: [['a', 'uh']], i: [['i', 'ie']], to: [['t', 't'], ['o', 'uu']],
  do: [['d', 'd'], ['o', 'uu']], into: [['i', 'i'], ['n', 'n'], ['t', 't'], ['o', 'uu']],
  is: [['i', 'i'], ['s', 'z']], as: [['a', 'a'], ['s', 'z']], was: [['w', 'w'], ['a', 'o'], ['s', 'z']],
  of: [['o', 'u'], ['f', 'v']], off: [['o', 'o'], ['ff', 'f']],
  you: [['y', 'y'], ['ou', 'uu']], your: [['y', 'y'], ['our', 'or']], two: [['tw', 't'], ['o', 'uu']],
  are: [['are', 'ar']], were: [['w', 'w'], ['ere', 'ur']], her: [['h', 'h'], ['er', 'ur']],
  have: [['h', 'h'], ['a', 'a'], ['ve', 'v']], give: [['g', 'g'], ['i', 'i'], ['ve', 'v']],
  live: [['l', 'l'], ['i', 'i'], ['ve', 'v']], love: [['l', 'l'], ['o', 'u'], ['ve', 'v']],
  come: [['c', 'k'], ['o', 'u'], ['me', 'm']], some: [['s', 's'], ['o', 'u'], ['me', 'm']],
  done: [['d', 'd'], ['o', 'u'], ['ne', 'n']], none: [['n', 'n'], ['o', 'u'], ['ne', 'n']],
  gone: [['g', 'g'], ['o', 'o'], ['ne', 'n']], one: [['o', 'w'], ['n', 'u'], ['e', 'n']],
  gem: [['g', 'j'], ['e', 'e'], ['m', 'm']], gel: [['g', 'j'], ['e', 'e'], ['l', 'l']],
  germ: [['g', 'j'], ['er', 'ur'], ['m', 'm']], gym: [['g', 'j'], ['y', 'i'], ['m', 'm']],
  giant: [['g', 'j'], ['i', 'ie'], ['a', 'uh'], ['n', 'n'], ['t', 't']],
  ginger: [['g', 'j'], ['in', 'i'], ['g', 'j'], ['er', 'ur']],
  gentle: [['g', 'j'], ['e', 'e'], ['n', 'n'], ['t', 't'], ['le', 'l']],
  magic: [['m', 'm'], ['a', 'a'], ['g', 'j'], ['i', 'i'], ['c', 'k']],
  that: [['th', 'dh'], ['a', 'a'], ['t', 't']], this: [['th', 'dh'], ['i', 'i'], ['s', 's']],
  then: [['th', 'dh'], ['e', 'e'], ['n', 'n']], them: [['th', 'dh'], ['e', 'e'], ['m', 'm']],
  than: [['th', 'dh'], ['a', 'a'], ['n', 'n']], these: [['th', 'dh'], ['e', 'ee'], ['se', 'z']],
  those: [['th', 'dh'], ['o', 'oe'], ['se', 'z']],
  who: [['wh', 'h'], ['o', 'uu']], whose: [['wh', 'h'], ['o', 'uu'], ['se', 'z']], whole: [['wh', 'h'], ['o', 'oe'], ['le', 'l']],
  what: [['wh', 'w'], ['a', 'o'], ['t', 't']], want: [['w', 'w'], ['a', 'o'], ['n', 'n'], ['t', 't']],
  said: [['s', 's'], ['ai', 'e'], ['d', 'd']], says: [['s', 's'], ['ay', 'e'], ['s', 'z']],
  they: [['th', 'dh'], ['ey', 'ae']], their: [['th', 'dh'], ['eir', 'air']], there: [['th', 'dh'], ['ere', 'air']],
  where: [['wh', 'w'], ['ere', 'air']], here: [['h', 'h'], ['ere', 'ear']],
  been: [['b', 'b'], ['ee', 'i'], ['n', 'n']], both: [['b', 'b'], ['o', 'oe'], ['th', 'th']],
  put: [['p', 'p'], ['u', 'oo'], ['t', 't']], pull: [['p', 'p'], ['u', 'oo'], ['ll', 'l']], full: [['f', 'f'], ['u', 'oo'], ['ll', 'l']],
  push: [['p', 'p'], ['u', 'oo'], ['sh', 'sh']], could: [['c', 'k'], ['oul', 'oo'], ['d', 'd']],
  would: [['w', 'w'], ['oul', 'oo'], ['d', 'd']], should: [['sh', 'sh'], ['oul', 'oo'], ['d', 'd']],
  does: [['d', 'd'], ['oe', 'u'], ['s', 'z']], goes: [['g', 'g'], ['oe', 'oe'], ['s', 'z']],
  go: [['g', 'g'], ['o', 'oe']], so: [['s', 's'], ['o', 'oe']], no: [['n', 'n'], ['o', 'oe']],
  he: [['h', 'h'], ['e', 'ee']], she: [['sh', 'sh'], ['e', 'ee']], we: [['w', 'w'], ['e', 'ee']],
  me: [['m', 'm'], ['e', 'ee']], be: [['b', 'b'], ['e', 'ee']],
  my: [['m', 'm'], ['y', 'ie']], by: [['b', 'b'], ['y', 'ie']], why: [['wh', 'w'], ['y', 'ie']],
  find: [['f', 'f'], ['i', 'ie'], ['n', 'n'], ['d', 'd']], kind: [['k', 'k'], ['i', 'ie'], ['n', 'n'], ['d', 'd']],
  mind: [['m', 'm'], ['i', 'ie'], ['n', 'n'], ['d', 'd']], child: [['ch', 'ch'], ['i', 'ie'], ['l', 'l'], ['d', 'd']],
  old: [['o', 'oe'], ['l', 'l'], ['d', 'd']], cold: [['c', 'k'], ['o', 'oe'], ['l', 'l'], ['d', 'd']],
  gold: [['g', 'g'], ['o', 'oe'], ['l', 'l'], ['d', 'd']], most: [['m', 'm'], ['o', 'oe'], ['s', 's'], ['t', 't']],
  book: [['b', 'b'], ['oo', 'oo'], ['k', 'k']], look: [['l', 'l'], ['oo', 'oo'], ['k', 'k']],
  took: [['t', 't'], ['oo', 'oo'], ['k', 'k']], good: [['g', 'g'], ['oo', 'oo'], ['d', 'd']],
  foot: [['f', 'f'], ['oo', 'oo'], ['t', 't']], wood: [['w', 'w'], ['oo', 'oo'], ['d', 'd']],
  cook: [['c', 'k'], ['oo', 'oo'], ['k', 'k']], hook: [['h', 'h'], ['oo', 'oo'], ['k', 'k']],
  stood: [['st', 's'], ['oo', 'oo'], ['d', 'd']], hood: [['h', 'h'], ['oo', 'oo'], ['d', 'd']],
  cow: [['c', 'k'], ['ow', 'ow']], how: [['h', 'h'], ['ow', 'ow']], now: [['n', 'n'], ['ow', 'ow']],
  down: [['d', 'd'], ['ow', 'ow'], ['n', 'n']], town: [['t', 't'], ['ow', 'ow'], ['n', 'n']],
  brown: [['br', 'b'], ['ow', 'ow'], ['n', 'n']], owl: [['ow', 'ow'], ['l', 'l']],
}

export function decodeWord(raw) {
  const w = String(raw || '').toLowerCase().replace(/[^a-z]/g, '')
  if (!w) return []
  if (EXC[w]) return EXC[w].filter(([t]) => t !== '').map(([text, code]) => ({ text, code }))

  const out = []
  let i = 0
  const n = w.length
  while (i < n) {
    const c = w[i]
    const rest = w.slice(i)

    // magic-e at the end of the word: vowel + single consonant + e (cake, kite, face)
    if (isVowel(c) && rest.length === 3 && !isVowel(w[i + 1]) && w[i + 2] === 'e' && w[i + 1] !== 'w') {
      out.push({ text: c, code: LONG[c] })
      out.push({ text: w[i + 1] + 'e', code: magicEConsonant(w[i + 1]) })
      i += 3
      continue
    }

    const three = w.slice(i, i + 3)
    if (TRI[three]) { out.push({ text: three, code: TRI[three] }); i += 3; continue }

    const two = w.slice(i, i + 2)
    if (DI[two] && !(two === 'qu')) { out.push({ text: two, code: DI[two] }); i += 2; continue }
    if (two === 'qu') { out.push({ text: 'qu', code: 'kw' }); i += 2; continue }
    if (two === 'mb' && i + 2 === n) { out.push({ text: 'mb', code: 'm' }); i += 2; continue } // silent b (lamb)

    out.push({ text: c, code: single(w, i) })
    i += 1
  }
  return out
}
